<template>
  <div class="fcom_calendar_view">
    <CalendarHeader
      :current-date="currentDate"
      :view-type="viewMode"
      :week-start-day="weekStartDay"
      @navigate-previous="navigatePrevious"
      @navigate-next="navigateNext"
      @go-today="goToToday"
      @view-change="switchView"
      @month-select="selectMonth"
    />

    <div class="fcom_calendar_body">
      <Transition name="fcom_view_switch" mode="out-in">
        <MonthCalendar
          v-if="viewMode === 'month'"
          key="month"
          :current-date="currentDate"
          :events="events"
          :week-start-day="weekStartDay"
          @select-date="onSelectDate"
          @select-event="onSelectEvent"
          @event-hover="onEventHover"
          @event-leave="onEventLeave"
        />
        <WeekCalendar
          v-else
          key="week"
          :current-date="currentDate"
          :events="events"
          :week-start-day="weekStartDay"
          :start-hour="6"
          :end-hour="22"
          :slot-height="60"
          @select-slot="onSelectSlot"
          @select-event="onSelectEvent"
          @event-hover="onEventHover"
          @event-leave="onEventLeave"
        />
      </Transition>
    </div>

    <!-- Hover popup card -->
    <CalendarEventCard
      :event="hoverEvent"
      :visible="hoverVisible"
      :position="hoverPosition"
      @view="onCardView"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { DateTime } from 'luxon'
import { getWeekStart } from '@/utils/calendar.js'
import CalendarHeader    from './calendar/CalendarHeader.vue'
import MonthCalendar     from './calendar/MonthCalendar.vue'
import WeekCalendar      from './calendar/WeekCalendar.vue'
import CalendarEventCard from './calendar/CalendarEventCard.vue'

const props = defineProps({
  events:       { type: Array,  default: () => [] },
  weekStartDay: { type: String, default: 'monday' },
  initialView:  { type: String, default: 'month' },
})

const emit = defineEmits(['select-event', 'select-date', 'view-change', 'date-change'])

const currentDate = ref(DateTime.now())
const viewMode    = ref(props.initialView)
const hoverEvent  = ref(null)
const hoverVisible = ref(false)
const hoverPosition = ref(null)
let   hoverTimer  = null

defineExpose({ currentDate, viewMode, navigatePrevious, navigateNext, goToToday, switchView })

onMounted(() => {
  const url  = new URL(window.location.href)
  const view = url.searchParams.get('view')
  if (view && ['month', 'week'].includes(view)) viewMode.value = view

  const month = url.searchParams.get('month')
  if (month) {
    const d = DateTime.fromFormat(month, 'yyyy-MM')
    if (d.isValid) currentDate.value = d
  }

  const week = url.searchParams.get('week')
  if (week) {
    const d = DateTime.fromFormat(week, 'yyyy-MM-dd')
    if (d.isValid) currentDate.value = d
  }
})

function syncUrl() {
  const url = new URL(window.location.href)
  url.searchParams.set('view', viewMode.value)
  if (viewMode.value === 'month') {
    url.searchParams.set('month', currentDate.value.toFormat('yyyy-MM'))
    url.searchParams.delete('week')
  } else {
    const ws = getWeekStart(currentDate.value, props.weekStartDay)
    url.searchParams.set('week', ws.toFormat('yyyy-MM-dd'))
    url.searchParams.delete('month')
  }
  window.history.replaceState({}, '', url)
}

function navigatePrevious() {
  currentDate.value = viewMode.value === 'month'
    ? currentDate.value.minus({ months: 1 })
    : currentDate.value.minus({ weeks: 1 })
  emit('date-change', currentDate.value)
  syncUrl()
}

function navigateNext() {
  currentDate.value = viewMode.value === 'month'
    ? currentDate.value.plus({ months: 1 })
    : currentDate.value.plus({ weeks: 1 })
  emit('date-change', currentDate.value)
  syncUrl()
}

function goToToday() {
  currentDate.value = DateTime.now()
  emit('date-change', currentDate.value)
  syncUrl()
}

function selectMonth(date) {
  currentDate.value = date.startOf('month')
  emit('date-change', currentDate.value)
  syncUrl()
}

function switchView(view) {
  if (viewMode.value !== view) {
    viewMode.value = view
    emit('view-change', view)
    syncUrl()
  }
}

function onSelectDate(date) {
  emit('select-date', date)
}

function onSelectEvent(ev) {
  emit('select-event', ev)
}

function onSelectSlot(slot) {
  emit('select-date', slot.date)
}

function onEventHover({ event, rect }) {
  clearTimeout(hoverTimer)
  hoverTimer = setTimeout(() => {
    hoverEvent.value = event
    hoverVisible.value = true

    const CARD_W = 280, CARD_H = 200, GAP = 10
    let x = rect.right + GAP
    let placement = 'right'
    if (x + CARD_W > window.innerWidth) {
      x = rect.left - GAP
      placement = 'left'
    }
    let y = rect.top + rect.height / 2 - CARD_H / 2
    y = Math.max(GAP, Math.min(y, window.innerHeight - CARD_H - GAP))
    hoverPosition.value = { x, y, placement }
  }, 200)
}

function onEventLeave() {
  clearTimeout(hoverTimer)
  hoverTimer = setTimeout(() => {
    hoverVisible.value = false
    hoverEvent.value   = null
  }, 100)
}

function onCardView() {
  if (hoverEvent.value) {
    emit('select-event', hoverEvent.value)
    hoverVisible.value = false
  }
}
</script>
